import React, { useCallback, useEffect, useState } from "react";
import Spacing from "../Spacing";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { can_visit, debounce, getPractice, notify } from "../../helpers";
import { Plus, Search } from "lucide-react";
import DataTablePagination from "../DataTablePagination";
import { deleteBulkPassword, deletePassword, fetchAllPasswords } from "../../services/passwordManager";
import PasswordManagerDataTable from "../DataTable/PasswordManagerDataTable";
import AddAndEditPasswordModal from "../Modal/AddAndEditPasswordModal";
import DeleteModal from "../Modal/DeleteModal";
import BulkDeleteModal from "../Modal/BulkDeleteModal";
import SharePasswordModal from "../Modal/SharePasswordModal";

export default function PasswordManager({selectedPracticeChange, fetchChangePracticeData}) {
  let account_id = getPractice("account_id");
  let location_id = getPractice("location_id");
  let user_id = getPractice("user_id");
  if(selectedPracticeChange != null && fetchChangePracticeData){
    account_id = selectedPracticeChange?.account_id;
    location_id = selectedPracticeChange?.location_id;
  }

  const [enableLoader, setEnableLoader] = useState(false);
  const [passwordData, setPasswordData] = useState([]);
  const [addNewPassword, setAddNewPassword] = useState(false);
  const [passwordEditData, setPasswordEditData] = useState({});
  const [editPassword, setEditPassword] = useState(false);

  const defaultFilter = {
    account_id: account_id,
    location_id: location_id,
    user_id: user_id,
    page: 1,
    per_page: 10,
    search_term: "",
    order_by: [],
  };
  const [filter, setFilter] = useState(defaultFilter);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedPasswordData, setSelectedPasswordData] = useState([]);
  const [globalCheckedIds, setGlobalCheckedIds] = useState(new Set());
  const [passwordDeletedId, setPasswordDeletedId] = useState(null);
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [activeBulkDeleteModal, setActiveBulkDeleteModal] = useState(false);
  const [passwordShareData, setPasswordSharedata] = useState({});
  const [openSharePasswordModal, setOpenSharePasswordModal] = useState(false);
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);

  const [dataCount, setDataCount] = useState({
    totalCount: 0,
    totalPages: 0,
  });
  const [checkedIds, setCheckedIds] = useState([]);
  const [checkSelected, setCheckSelected] = useState(checkedIds.length > 0);

  useEffect(() => {
    fetchPasswordData(filter);
  }, [filter]);

  const fetchPasswordData = async (allfilter) => {
    setEnableLoader(true);
    try {
      if(can_visit('password_manager')) {
        const response = await fetchAllPasswords(allfilter);
        if (response) {
          if(response?.data){
            setPasswordData(response?.data?.results);
            setDataCount({
              totalCount: response?.data?.count,
              totalPages: response?.data?.total_pages,
            });
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setEnableLoader(false);
    }
  };

  useEffect(() => {
    setCheckSelected(checkedIds.length > 0 ? true : false);
  }, [checkedIds]);

  const debouncedSetFilter = useCallback(
    debounce((newFilter) => setFilter(newFilter), 300), []
  );

  const handleChangeSearchTerm = (event) => {
    const newFilter = { ...filter, search_term: event.target.value, page: 1 };
    setEnableLoader(true);
    debouncedSetFilter(newFilter);
  };

  const handlePageChange = (newPage) => {
    setFilter({ ...filter, page: newPage });
    setEnableLoader(true);
  };

  const handleChangeRowsPerPage = (value) => {
    setFilter({
      ...filter,
      page: 1,
      per_page: value,
    });
    setEnableLoader(true);
  };

  const handleCheckboxChange = (id, rowData) => {
    const isChecked = checkedIds.includes(id);
    if (isChecked) {
      setCheckedIds(checkedIds.filter((checkedId) => checkedId !== id));
    } else {
      setCheckedIds([...checkedIds, id]);
    }
    setPasswordData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, checked: !row.checked } : row
      )
    );
    setGlobalCheckedIds((prevCheckedIds) => {
      const updatedCheckedIds = new Set(prevCheckedIds);
      if (updatedCheckedIds.has(id)) {
        updatedCheckedIds.delete(id);
        setSelectedPasswordData((prevData) => prevData.filter((data) => data.id !== id));
      } else {
        updatedCheckedIds.add(id);
        setSelectedPasswordData((prevData) => [...prevData, rowData]);
      }
      return updatedCheckedIds;
    });
  };

  const handleSelectAll = (currentPageData) => {
    if (selectAll) {
      const newCheckedIds = checkedIds.filter(
        (id) => !currentPageData.some((row) => row.id === id)
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.delete(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPasswordData((prevData) =>
        prevData.filter((data) => !currentPageData.some((row) => row.id === data.id))
      );
    } else {
      const newCheckedIds = Array.from(
        new Set([...checkedIds, ...currentPageData.map((row) => row.id)])
      );

      setCheckedIds(newCheckedIds);
      setGlobalCheckedIds((prevCheckedIds) => {
        const updatedCheckedIds = new Set(prevCheckedIds);
        currentPageData.forEach((row) => updatedCheckedIds.add(row.id));
        return updatedCheckedIds;
      });
  
      setSelectedPasswordData((prevData) => [
        ...prevData,
        ...currentPageData.filter(
          (row) => !prevData.some((data) => data.id === row.id)
        ),
      ]);
    }

    setSelectAll(!selectAll);

    setPasswordData((prevData) =>
      prevData.map((row) => ({
        ...row,
        checked: !selectAll,
      }))
    );
  };

  const handleDeleteRecord = async (id) => {
    setEnableLoader(true);
    try {
      if(can_visit('password_manager') && can_visit("password_manager_delete")) {
        const response = await deletePassword( id, account_id, location_id );
        if(response){
          if(response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.status === "success") {
              notify("success", response?.data?.message, 0.5);
              await fetchPasswordData(filter);
            } else {
              notify("error", response?.data?.message);
            }
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setEnableLoader(false);
    }
  };

  const handleBulkDeleteRecord = async (e) => {
    try {
      setEnableLoader(true);
      const deleteData = {
        account_id: account_id,
        location_id: location_id,
        selected_ids: checkedIds,
      };

      if(can_visit('password_manager') && can_visit("password_manager_delete")) {
        const response = await deleteBulkPassword(deleteData);
        if (response) {
          if (response?.detail) {
            notify("error", response?.detail);
          } else {
            if(response?.data?.success){
              notify("success", response?.data?.success, 0.5);
              setSelectedPasswordData([]);
              setCheckedIds([]);
              setGlobalCheckedIds(new Set());
              await fetchPasswordData(filter);
            } else {
              notify("error", response?.data?.success);
            }
          }
        }
      }
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 1);
      }
    } finally {
      setEnableLoader(false);
    }
  };

  useDocumentTitle("Password Manager");

  return (
    <>
      <div className="docpro_page_heading_wrap mb-0">
        <h2 className="docpro_page_heading docpro_fs_24 docpro_mb_0">
          Password Manager
        </h2>
      </div>
      <Spacing lg="15" sm="20" />

      <div className="docpro_data_table_wrap">
        <div className="docpro_data_table_headings docpro_grid_gap_10">
          <form className="docpro_search_form width_100">
            <input
              type="text"
              placeholder="Search"
              className="docpro_search_input"
              onChange={handleChangeSearchTerm}
            />
            <button className="docpro_search_btn">
              <Search width={20} height={20} />
            </button>
          </form>

          {can_visit("password_manager_create") && (
            <button 
              type="button"
              className="docpro_bordered_btn docpro_fw_600 docpro_primary_color py-2"
              onClick={() => {
                setAddNewPassword(true);
                setPreviousScrollPosition(window.scrollY);
                window.scrollTo(0, 0);
              }}
            >
              <Plus width={20} height={20} />
              Add Password
            </button>
          )}
        </div>

        <PasswordManagerDataTable
          user_id={user_id}
          tableData={passwordData}
          handleSelectAll={() => handleSelectAll(passwordData)}
          handleCheckboxChange={handleCheckboxChange}
          checkedIds={checkedIds}
          setCheckedIds={setCheckedIds}
          checkSelected={checkSelected}
          reloadData={fetchPasswordData}
          reloadDataFilter={filter}
          dataLoading={enableLoader}
          setDataLoading={setEnableLoader}
          globalCheckedIds={globalCheckedIds}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPasswordData={selectedPasswordData}
          setSelectedPasswordData={setSelectedPasswordData}
          setPasswordEditData={setPasswordEditData}
          setEditPassword={setEditPassword}
          setPasswordDeletedId={setPasswordDeletedId}
          setActiveDeleteModal={setActiveDeleteModal}
          setActiveBulkDeleteModal={setActiveBulkDeleteModal}
          setPasswordSharedata={setPasswordSharedata}
          setOpenSharePasswordModal={setOpenSharePasswordModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
        {(passwordData?.length > 0 && !enableLoader) && (
          <DataTablePagination
            currentPage={filter.page}
            totalPages={dataCount.totalPages}
            onPageChange={handlePageChange}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRecords={dataCount.totalCount}
            currentPageData={filter.per_page}
          />
        )}
      </div>


      {addNewPassword &&
        <AddAndEditPasswordModal
          account_id = {account_id}
          location_id = {location_id}
          user_id = {user_id}
          reloadData = {fetchPasswordData}
          reloadDataFilter = {filter}
          editModal = {false}
          activeModal = {addNewPassword}
          setActiveModal = {setAddNewPassword}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {editPassword &&
        <AddAndEditPasswordModal
          account_id = {account_id}
          location_id = {location_id}
          user_id = {user_id}
          reloadData = {fetchPasswordData}
          reloadDataFilter = {filter}
          editValue = {passwordEditData}
          editModal = {true}
          activeModal = {editPassword}
          setActiveModal = {setEditPassword}
          previousScrollPosition={previousScrollPosition}
        />
      }

      {activeDeleteModal && (
        <DeleteModal
          handleDelete={() => handleDeleteRecord(passwordDeletedId)}
          name="this password"
          activeModal={activeDeleteModal}
          setActiveModal={setActiveDeleteModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}

      {activeBulkDeleteModal && (
        <BulkDeleteModal
          handleDelete={() => handleBulkDeleteRecord()}
          name={`${checkedIds?.length} records`}
          activeModal={activeBulkDeleteModal}
          setActiveModal={setActiveBulkDeleteModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {openSharePasswordModal && (
        <SharePasswordModal
          account_id={account_id}
          location_id={location_id}
          user_id={user_id}
          reloadData = {fetchPasswordData}
          reloadDataFilter = {filter}
          shareValue = {passwordShareData}
          activeModal={openSharePasswordModal}
          setActiveModal={setOpenSharePasswordModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}
    </>
  );
}
